import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@tailoring-crm/ui';
import { useSession } from '../../lib/session/session-context.js';
import { bottomNavFor, isNavItemActive } from './nav-items.js';

/**
 * Phone navigation: a fixed bar at the bottom, where the thumb already is.
 *
 * At 360px — the narrowest screen this product supports — five items get 72px each, which
 * fits a 24px icon and a label without truncation. The bar pads itself past the iPhone home
 * indicator with `pb-safe`, and `AppShell` pads the page by the same height so the last row
 * of a list is never trapped underneath it.
 *
 * `bottomNavFor` rather than `navFor`: the sidebar carries an entry or two this bar has no room
 * for. Nothing becomes unreachable by being left out — see `NavItem.sidebarOnly` for where each
 * one is reached from instead. Note that `notFor` is deliberately NOT applied here: it exists to
 * stop two entries lighting up at once, and the entry it defers to is not in this bar.
 */
export function BottomNav() {
  const { t } = useTranslation();
  const items = bottomNavFor(useSession()?.role);
  const { pathname } = useLocation();

  return (
    <nav
      aria-label={t('nav.primaryLabel')}
      className="pb-safe fixed inset-x-0 bottom-0 z-30 grid grid-flow-col border-t border-sidebar-border bg-sidebar md:hidden"
    >
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end ?? false}
          // The first-order tour starts by pointing here (see components/onboarding/tour).
          data-tour={item.to === '/customers' ? 'nav-customers' : undefined}
          className={cn(
            'flex h-16 flex-col items-center justify-center gap-1 px-1 text-[0.6875rem] font-medium transition-colors',
            isNavItemActive(item, pathname)
              ? 'text-sidebar-accent-foreground'
              : 'text-sidebar-foreground',
          )}
        >
          <item.icon aria-hidden="true" className="size-5 shrink-0" />
          <span className="max-w-full truncate">{t(item.labelKey)}</span>
        </NavLink>
      ))}
    </nav>
  );
}
